import React, { useState } from "react";
import { FaSearch, FaClock, FaCalendar, FaServer } from "react-icons/fa";
import NotifyModal from "../components/NotifyModal";
import '../styles/PlayerTracker.css';

const PlayerTracker = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [playerData, setPlayerData] = useState(null);
    const [error, setError] = useState(null);
    const [notifyConfig, setNotifyConfig] = useState({
        show: false,
        type: '',
        message: ''
    });

    const showNotification = (message, type) => {
        setNotifyConfig({
            show: true,
            type,
            message
        });
    };

    const closeNotification = () => {
        setNotifyConfig({
            show: false,
            type: '',
            message: ''
        });
    };

    const parseMinecraftDescription = (description) => {
        if (!description) return <span>N/A</span>;

        const colorMap = {
            '§0': '#000000',
            '§1': '#0000AA',
            '§2': '#00AA00',
            '§3': '#00AAAA',
            '§4': '#AA0000',
            '§5': '#AA00AA',
            '§6': '#FFAA00',
            '§7': '#AAAAAA',
            '§8': '#555555',
            '§9': '#5555FF',
            '§a': '#55FF55',
            '§b': '#55FFFF',
            '§c': '#FF5555',
            '§d': '#FF55FF',
            '§e': '#FFFF55',
            '§f': '#FFFFFF',
            '§l': 'bold',
            '§o': 'italic',
            '§n': 'underline',
            '§m': 'line-through',
            '§r': 'reset'
        };

        let currentStyles = {
            color: '#FFFFFF',
            fontWeight: 'normal',
            fontStyle: 'normal',
            textDecoration: 'none'
        };
        const segments = [];
        let currentSegment = '';
        let i = 0;

        while (i < description.length) {
            if (description[i] === '§' && i + 1 < description.length) {
                const code = description.slice(i, i + 2);
                i += 2;

                if (colorMap[code]) {
                    if (currentSegment) {
                        segments.push(
                            <span
                                key={segments.length}
                                style={{
                                    color: currentStyles.color,
                                    fontWeight: currentStyles.fontWeight,
                                    fontStyle: currentStyles.fontStyle,
                                    textDecoration: currentStyles.textDecoration
                                }}
                            >
                                {currentSegment}
                            </span>
                        );
                        currentSegment = '';
                    }

                    if (colorMap[code] === 'reset') {
                        currentStyles = {
                            color: '#FFFFFF',
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none'
                        };
                    } else if (code === '§l') {
                        currentStyles.fontWeight = 'bold';
                    } else if (code === '§o') {
                        currentStyles.fontStyle = 'italic';
                    } else if (code === '§n') {
                        currentStyles.textDecoration = 'underline';
                    } else if (code === '§m') {
                        currentStyles.textDecoration = 'line-through';
                    } else {
                        currentStyles.color = colorMap[code];
                    }
                }
            } else {
                currentSegment += description[i];
                i++;
            }
        }

        if (currentSegment) {
            segments.push(
                <span
                    key={segments.length}
                    style={{
                        color: currentStyles.color,
                        fontWeight: currentStyles.fontWeight,
                        fontStyle: currentStyles.fontStyle,
                        textDecoration: currentStyles.textDecoration
                    }}
                >
                    {currentSegment}
                </span>
            );
        }

        return <span>{segments}</span>;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) {
            setError("Please enter a username");
            showNotification("Please enter a username", "error");
            console.warn("Empty search query");
            return;
        }

        setIsLoading(true);
        setError(null);
        setPlayerData(null);

        const url = `https://apibeta.resourcedecrypt.cc/api/minecraft/player-history?name=${encodeURIComponent(searchQuery)}`;
        console.debug("Sending request to:", url);

        try {
            const response = await fetch(url);
            console.debug("Response status:", response.status, "OK:", response.ok);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Response error:", errorText);
                showNotification(`Failed to fetch player data: ${errorText}`, "error");
                throw new Error(`Failed to fetch player data: ${errorText}`);
            }
            const data = await response.json();
            console.debug("Received data:", data);
            if (data.sessions.length === 0) {
                showNotification("No server history found for this player", "error");
                throw new Error("No server history found for this player");
            }
            setPlayerData(data);
            showNotification(`Player ${searchQuery}'s history loaded successfully!`, "success");
        } catch (err) {
            console.error("Fetch error:", err);
            setError(err.message || "Failed to fetch player data");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <NotifyModal
                type={notifyConfig.type}
                message={notifyConfig.message}
                isVisible={notifyConfig.show}
                onClose={closeNotification}
            />

            <section className="hero">
                <div className="hero-content">
                    <div className="hero-eyebrow">
                        <span className="status-indicator"></span>
                        Player History Tracker
                    </div>
                    <h1>
                        <span className="gradient-text">Player Tracker</span>
                    </h1>
                    <p className="hero-description">
                        Track player activity across Minecraft servers. Enter a username to get started.
                    </p>
                </div>
            </section>

            <section className="tracker-section">
                <div className="tracker-container">
                    <div className="search-container">
                        <form onSubmit={handleSubmit} className="search-form">
                            <input
                                type="text"
                                placeholder="Enter player username..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            <button type="submit" className="search-btn" disabled={isLoading}>
                                <FaSearch />
                                {isLoading ? "Searching..." : "Search"}
                            </button>
                        </form>
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {playerData && (
                        <div className="player-info-container">
                            <div className="player-header">
                                <div className="player-avatar">
                                    <img
                                        src={`https://minotar.net/avatar/${playerData.playerName}/64`}
                                        alt={playerData.playerName}
                                    />
                                </div>
                                <div className="player-details">
                                    <h2>{playerData.playerName}</h2>
                                    <p className="uuid-text">{playerData.playerUuid}</p>
                                    <div className="player-stats">
                                        <span>
                                            <FaCalendar /> First seen: Unknown
                                        </span>
                                        <span>
                                            <FaClock /> Last active:{" "}
                                            {playerData.sessions.length > 0
                                                ? new Date(playerData.sessions[0].lastSession * 1000).toLocaleString()
                                                : "Unknown"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="server-history">
                                <h3>Server History</h3>
                                {playerData.sessions.length === 0 ? (
                                    <p>No server history found.</p>
                                ) : (
                                    <div className="server-grid">
                                        {playerData.sessions.map((session, index) => (
                                            <div key={index} className="server-card">
                                                <div className="server-card-header">
                                                    <FaServer className="server-icon" />
                                                    <h4>{`${session.ip}:${session.port}`}</h4>
                                                    <span className="server-type">Java</span>
                                                </div>
                                                <div className="server-stats">
                                                    <div className="stat-item">
                                                        <FaClock />
                                                        <span>
                                                            Last Seen: {new Date(session.lastSession * 1000).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="stat-item description-item">
                                                        <FaServer />
                                                        <span>Description: {parseMinecraftDescription(session.serverDescription)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default PlayerTracker;